import React, { Fragment, useEffect, useState } from "react";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import styled from "styled-components";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import CaseService from "../../../services/CaseService";
import { AssigninBulkCaseId } from "../../../helpers/validationSchema";
import { Formik } from "formik";
import FormField from "../../common/FormField/FormField";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import { Modal } from "@material-ui/core";
import NeutralModal from "./NeutralModal";
import AddIcon from "@material-ui/icons/Add";
import { CustomCheckbox } from "../../common/FormInputs";
import * as XLSX from "xlsx";
import ResponseModal from "./ResponseModal";
import {
  Overlay,
  ModalCard,
  ModalHeader,
  ModalBody,
  ModalTitle,
  ModeBtn,
  ModeToggle,
  StyledForm,
  FooterActions,
  FormSection,
  ExcelZone,
  HeaderAccent,
  HeaderContent,
  HiddenInput,
  IconBadge,
  CloseBtn,
  SectionLabel,
  UploadCheck,
  UploadCloud,
  UploadInner,
  UploadPrimary,
  UploadSub,
  Divider,
} from "../../common/MutipleHearings";

const inputModes = { MANUAL: "manual", EXCEL: "excel" };

const inputs = [
  {
    type: "number",
    name: "startCaseId",
    label: "From CaseId",
    required: true,
    autoFocus: true,
  },
  { type: "number", name: "endCaseId", label: "End CaseId", required: true },
  {
    type: "text",
    name: "exceptCaseIds",
    label: "Except Case IDs (comma separated)",
    required: false,
    placeholder: "e.g., 102, 109, 120",
    helperText: "Optional: exclude specific cases from the selected range",
  },
];

const Index = ({ drawer, setDrawer }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [neutralList, setNeutralList] = useState(null);
  const [state, setState] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [mail, setMail] = useState(false);
  const [mode, setMode] = useState(inputModes.MANUAL);
  const [excelCaseIds, setExcelCaseIds] = useState([]);
  const [showResponseModal, setShowResponseModal] = useState({ state: false });
  const isViewOnly = drawer?.assignType === "viewOnly" ? true : false;

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true });
      if (!neutralList?.id) {
        enqueueSnackbar("Please select a neutral", { variant: "warning" });
        return;
      }
      let payload = {};
      const selectedUser = {
        email: neutralList?.email,
        name: neutralList?.name,
        mobile: neutralList?.mobile,
        id: neutralList?.id,
        avatarUrl: neutralList?.avatarUrl,
      };
      const userKey = isViewOnly ? "passiveNeutrals" : "requestedMediators";

      if (mode === inputModes.MANUAL) {
        payload = {
          startCaseId: values?.startCaseId,
          endCaseId: values?.endCaseId,
          exceptCaseIds: values?.exceptCaseIds,
          resolutionKind: values.resolutionKind,
          ...(isViewOnly ? {} : { individualMail: values?.individualMail }),
          [userKey]: [selectedUser],
        };
      } else {
        if (excelCaseIds.length === 0) {
          enqueueSnackbar("Please upload a valid Excel file with case IDs", {
            variant: "warning",
          });
          return;
        }
        payload = {
          caseIds: excelCaseIds.map((id) => Number(id)),
          resolutionKind: values.resolutionKind,
          ...(isViewOnly ? {} : { individualMail: values?.individualMail }),
          [userKey]: [selectedUser],
        };
      }
      const response = await CaseService.NeutralBulkCase(payload);
      if (response) {
        setShowResponseModal({ state: true, metaData: response });
        setDrawer(false);
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const handleFileUpload = (e, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;
    setFieldValue("fileName", file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (data.length === 0) {
          enqueueSnackbar("Excel file is empty", { variant: "error" });
          return;
        }
        const headers = (data[0] || []).map((h) =>
          (h || "").toString().toLowerCase().trim(),
        );
        const colIdx = headers.findIndex((h) =>
          [
            "caseid",
            "case id",
            "id",
            "case_id",
            "case-id",
            "case id/dispute no",
          ].includes(h),
        );
        if (colIdx === -1) {
          enqueueSnackbar(
            "Column not found. Please use one of: caseId, Case Id, id",
            { variant: "error" },
          );
          return;
        }
        const ids = data
          .slice(1)
          .map((row) => row[colIdx])
          .filter((id) => id !== undefined && id !== null && id !== "")
          .map(Number)
          .filter((id) => !isNaN(id) && id > 0);
        if (ids.length === 0) {
          enqueueSnackbar("No valid case IDs found in the file", {
            variant: "warning",
          });
          return;
        }
        setExcelCaseIds(ids);
        enqueueSnackbar(
          `${ids.length} case ID${ids.length > 1 ? "s" : ""} loaded successfully`,
          { variant: "success" },
        );
      } catch (err) {
        enqueueSnackbar(
          "Failed to read Excel file. Please try a valid .xlsx file.",
          { variant: "error" },
        );
      }
    };
    reader.onerror = () =>
      enqueueSnackbar("Error reading file", { variant: "error" });
    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  useEffect(() => {
    if (drawer) {
      setNeutralList(null);
      setStart("");
      setEnd("");
      setMail(false);
      setMode(inputModes.MANUAL);
      setExcelCaseIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer]);

  return (
    <Fragment>
      <Modal open={drawer} onClose={() => setDrawer(false)}>
        <Overlay>
          <ModalCard>
            {/* ── Header ── */}
            <ModalHeader>
              <HeaderAccent />
              <HeaderContent>
                <IconBadge>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </IconBadge>
                <ModalTitle>
                  {isViewOnly ? "Add View Only User" : "Assign Neutral"}
                </ModalTitle>
              </HeaderContent>
              <CloseBtn onClick={() => setDrawer(false)} aria-label="Close">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ModalHeader>

            {/* ── Formik Body ── */}
            <ModalBody>
              <Formik
                initialValues={{
                  startCaseId: start || "",
                  endCaseId: end || "",
                  exceptCaseIds: "",
                  neutralList: neutralList || "",
                  individualMail: mail,
                  fileName: "",
                  resolutionKind: "arbitration",
                }}
                onSubmit={onFormSubmit}
                validationSchema={
                  mode === inputModes.MANUAL ? AssigninBulkCaseId : undefined
                }
                validateOnBlur
                enableReinitialize
                validateOnChange
              >
                {({
                  values,
                  handleChange,
                  errors,
                  handleSubmit,
                  touched,
                  handleBlur,
                  setFieldValue,
                  isValid,
                }) => (
                  <StyledForm onSubmit={handleSubmit}>
                    {/* Input Mode Toggle */}
                    <FormSection>
                      <SectionLabel>Input Method</SectionLabel>
                      <ModeToggle>
                        <ModeBtn
                          type="button"
                          active={mode === inputModes.MANUAL}
                          onClick={() => {
                            setMode(inputModes.MANUAL);
                            setExcelCaseIds([]);
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Enter ID Range
                        </ModeBtn>
                        <ModeBtn
                          type="button"
                          active={mode === inputModes.EXCEL}
                          onClick={() => setMode(inputModes.EXCEL)}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          Upload Excel
                        </ModeBtn>
                      </ModeToggle>
                    </FormSection>

                    {/* Manual / Excel Inputs */}
                    <FormSection>
                      {mode === inputModes.MANUAL ? (
                        inputs.map((input, key) => (
                          <FormField
                            key={key}
                            white
                            {...{
                              input,
                              values,
                              errors,
                              touched,
                              handleBlur,
                              handleChange,
                              setFieldValue,
                            }}
                          />
                        ))
                      ) : (
                        <ExcelZone>
                          <label
                            htmlFor="excel-upload"
                            style={{ cursor: "pointer", display: "block" }}
                          >
                            <HiddenInput
                              id="excel-upload"
                              type="file"
                              accept=".xlsx, .xls"
                              onChange={(e) =>
                                handleFileUpload(e, setFieldValue)
                              }
                            />
                            <UploadInner loaded={excelCaseIds.length > 0}>
                              {excelCaseIds.length > 0 ? (
                                <>
                                  <UploadCheck>
                                    <svg
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  </UploadCheck>
                                  <UploadPrimary>
                                    {excelCaseIds.length} Case IDs Loaded
                                  </UploadPrimary>
                                  <UploadSub>Click to replace file</UploadSub>
                                </>
                              ) : (
                                <>
                                  <UploadCloud>
                                    <svg
                                      width="28"
                                      height="28"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <polyline points="16 16 12 12 8 16" />
                                      <line x1="12" y1="12" x2="12" y2="21" />
                                      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                                    </svg>
                                  </UploadCloud>
                                  <UploadPrimary>
                                    Drop Excel file or click to browse
                                  </UploadPrimary>
                                  <UploadSub>
                                    Column must be named <code>caseId</code>
                                  </UploadSub>
                                </>
                              )}
                            </UploadInner>
                          </label>
                        </ExcelZone>
                      )}
                      {[
                        {
                          type: "select",
                          name: "resolutionKind",
                          label: "Case Type",
                          required: true,
                          menuItems: [
                            { label: "Arbitration", value: "arbitration" },
                            { label: "Mediation", value: "mediation" },
                            { label: "Negotiation", value: "negotiation" },
                          ],
                        },
                      ].map((input, key) => (
                        <FormField
                          key={key}
                          menuItems={input.menuItems}
                          white
                          {...{
                            input,
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            setFieldValue,
                          }}
                        />
                      ))}
                    </FormSection>

                    <Divider />

                    {!isViewOnly && (
                      <>
                        <CheckRow>
                          <CustomCheckbox
                            name="individualMail"
                            checked={values.individualMail}
                            onChange={handleChange}
                          />
                          <CheckLabel htmlFor="individualMail">
                            Send mail individually to each case
                          </CheckLabel>
                        </CheckRow>

                        <Divider />
                      </>
                    )}

                    {/* Neutral Selection */}
                    <FormSection>
                      <SectionLabel>
                        {isViewOnly ? "View Only User" : "Neutral"}
                      </SectionLabel>
                      <NeutralCard
                        filled={!!neutralList?.name}
                        onClick={() => {
                          setState(true);
                          setStart(values?.startCaseId);
                          setEnd(values?.endCaseId);
                          setMail(values?.individualMail);
                        }}
                      >
                        <AddCircle filled={!!neutralList?.name}>
                          {neutralList?.name ? (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                          ) : (
                            <AddIcon style={{ fontSize: 18 }} />
                          )}
                        </AddCircle>
                        <NeutralInfo>
                          {neutralList?.name ? (
                            <>
                              <NeutralName>{neutralList.name}</NeutralName>
                              <NeutralEmail>{neutralList.email}</NeutralEmail>
                            </>
                          ) : (
                            <NeutralPlaceholder>
                              {isViewOnly
                                ? "Select View Only User"
                                : "Select Neutral"}
                            </NeutralPlaceholder>
                          )}
                        </NeutralInfo>
                        <ChevronIcon>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </ChevronIcon>
                      </NeutralCard>
                    </FormSection>

                    {/* Action Buttons */}
                    <FooterActions>
                      <PrimaryOutlinedCloseCTAButton
                        type="button"
                        onClick={() => setDrawer(false)}
                        style={{ flex: 1 }}
                      >
                        Cancel
                      </PrimaryOutlinedCloseCTAButton>
                      <PrimaryCTAButton
                        type="submit"
                        disabled={
                          !neutralList?.id ||
                          (mode === inputModes.MANUAL &&
                            (!isValid ||
                              !values?.startCaseId ||
                              !values?.endCaseId)) ||
                          (mode === inputModes.EXCEL &&
                            excelCaseIds.length === 0)
                        }
                        onClick={handleSubmit}
                        style={{ flex: 1 }}
                      >
                        Update
                      </PrimaryCTAButton>
                    </FooterActions>
                  </StyledForm>
                )}
              </Formik>
            </ModalBody>
          </ModalCard>
        </Overlay>
      </Modal>

      <ResponseModal
        open={showResponseModal?.state}
        onClose={() => {
          setShowResponseModal({ state: false });
          setDrawer(false);
        }}
        responseData={showResponseModal?.metaData}
      />

      <NeutralModal
        {...{ state, setState, neutralList, setNeutralList, isViewOnly }}
      />
    </Fragment>
  );
};

export default Index;

const CheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  background: #f9fafb;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 16px;
`;

const CheckLabel = styled.label`
  font-size: 13.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #374151;
  cursor: pointer;
`;

const NeutralCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid ${(p) => (p.filled ? `${COLORS.BTN_GREEN}40` : "#e5e7eb")};
  background: ${(p) => (p.filled ? `${COLORS.BTN_GREEN}06` : "#fafafa")};
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: ${COLORS.BTN_GREEN};
    background: ${COLORS.BTN_GREEN}08;
  }
`;

const AddCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(p) =>
    p.filled ? `${COLORS.BTN_GREEN}20` : `${COLORS.BTN_GREEN}15`};
  color: ${COLORS.BTN_GREEN};
  transition: all 0.2s;
`;

const NeutralInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const NeutralName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a2035;
  font-family: ${theme.fonts.primaryFontSemiBold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NeutralEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NeutralPlaceholder = styled.div`
  font-size: 13px;
  color: #9ca3af;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const ChevronIcon = styled.div`
  color: #9ca3af;
  flex-shrink: 0;
`;
